import React, { useEffect, useState, useCallback } from "react";
import { Table, Spinner, Button, Form, Row, Col, Badge, Alert as BsAlert } from "react-bootstrap";
import SafetyValutazioniService from "../../../services/safety/SafetyValutazioniService";
import LavoratoreSearchPicker from "../LavoratoreSearchPicker/LavoratoreSearchPicker";

const EMPTY = {
    id_tipo_val: "",
    id_lavoratore: "",
    titolo: "",
    responsabile: "",
    esito: "IN_CORSO",
    livello_rischio: "",
    numero_protocollo: "",
    data_valutazione: "",
    data_scadenza: "",
    periodicita_mesi: "",
    note: "",
};

const ESITI = [
    { value: "IN_CORSO", label: "In corso" },
    { value: "ADEGUATO", label: "Adeguato" },
    { value: "DA_MIGLIORARE", label: "Da migliorare" },
    { value: "NON_ADEGUATO", label: "Non adeguato" },
];

const LIVELLI = [
    { value: "", label: "— Non indicato —" },
    { value: "BASSO", label: "Basso" },
    { value: "MEDIO", label: "Medio" },
    { value: "ALTO", label: "Alto" },
    { value: "MOLTO_ALTO", label: "Molto alto" },
];

const VAL_HELP = {
    RISCHIO_TERZI: "Valutazione del rischio da aggressione, rapina, furto o danneggiamento da parte di terzi. Tipica per vigilanza e security.",
    LAVORO_NOTTURNO: "Analisi dei rischi legati ai turni notturni; collegata alla sorveglianza sanitaria dedicata.",
    STRESS_LC: "Valutazione dello stress lavoro-correlato (metodologia SLC / MDG 2010). Revisione consigliata ogni 2 anni.",
    LAVORATORE_ISOLATO: "Valutazione per lavoratori che operano in solitudine o senza contatto diretto con altri.",
    VDT: "Valutazione per operatori di centrale/videoterminali (postazioni CRM, monitoraggio).",
    TRASPORTO_VALORI: "Valutazione specifica per attività di trasporto e scorta valori.",
};

function fmtDate(d) {
    if (!d) return "—";
    const s = String(d).substring(0, 10);
    const p = s.split("-");
    if (p.length !== 3) return s;
    return `${p[2]}/${p[1]}/${p[0]}`;
}

function groupByCategoria(tipi) {
    const groups = {};
    tipi.forEach(t => {
        const cat = t.categoria || "Altri";
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(t);
    });
    return Object.entries(groups);
}

function statoBadge(stato, giorni) {
    const map = {
        SCADUTO:     { bg: "danger",  text: "light", label: "Revisione scaduta" },
        IN_SCADENZA: { bg: "warning", text: "dark",  label: "Revisione in scadenza" },
        VALIDO:      { bg: "success", text: "light", label: "Valida" },
        PERMANENTE:  { bg: "light",   text: "dark",  label: "Senza revisione" },
    };
    const m = map[stato] || map.PERMANENTE;
    return (
        <span>
            <Badge bg={m.bg} text={m.text} className={m.bg === "light" ? "border" : undefined}>{m.label}</Badge>
            {typeof giorni === "number" && (stato === "SCADUTO" || stato === "IN_SCADENZA") && (
                <span className="text-muted small ms-1">
                    ({giorni < 0 ? `${Math.abs(giorni)} gg fa` : `tra ${giorni} gg`})
                </span>
            )}
        </span>
    );
}

function esitoBadge(esito) {
    if (!esito) return <span className="text-muted">—</span>;
    const tone = esito === "ADEGUATO" ? "success"
        : esito === "NON_ADEGUATO" ? "danger"
        : esito === "DA_MIGLIORARE" ? "warning" : "secondary";
    const label = ESITI.find(e => e.value === esito)?.label || esito;
    return <Badge bg={tone} text={tone === "warning" ? "dark" : "light"}>{label}</Badge>;
}

export default function ValutazioniPanel({ idAzienda, settore }) {
    const [tipi, setTipi] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState(null);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    const loadTipi = useCallback(async () => {
        if (!idAzienda) { setTipi([]); return; }
        try {
            const resp = await SafetyValutazioniService.getTipi(idAzienda);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            if (Array.isArray(data)) setTipi(data);
            else if (data?.message || data?.error) {
                setTipi([]);
                setError(data.message || data.error);
            } else setTipi([]);
        } catch (err) {
            setTipi([]);
            const m = err?.message || err?.error?.message || "";
            setError(m.includes("NetworkError") || m.includes("Failed to fetch")
                ? "API non raggiungibile. Deploy backend (SafetyValutazioniController + Service) e auth.json."
                : (m || "Impossibile caricare il catalogo valutazioni."));
        }
    }, [idAzienda]);

    const loadItems = useCallback(async () => {
        if (!idAzienda) { setItems([]); return; }
        setLoading(true);
        setError(null);
        try {
            const resp = await SafetyValutazioniService.byAzienda(idAzienda);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            const m = err?.message || err?.error?.message || "";
            setError(m || "Errore nel caricamento delle valutazioni.");
        } finally {
            setLoading(false);
        }
    }, [idAzienda]);

    useEffect(() => { loadTipi(); }, [loadTipi]);
    useEffect(() => { loadItems(); }, [loadItems]);

    const openForm = () => {
        setForm(EMPTY);
        setFile(null);
        setError(null);
        setMsg(null);
        setShow(true);
    };

    const submit = async () => {
        if (!idAzienda) return;
        if (!form.id_tipo_val && !form.titolo) {
            setError("Seleziona un tipo o indica un titolo.");
            return;
        }
        setSaving(true);
        setError(null);
        setMsg(null);
        try {
            const fd = new FormData();
            fd.append("id_azienda", idAzienda);
            if (form.id_tipo_val)        fd.append("id_tipo_val", form.id_tipo_val);
            if (form.id_lavoratore)      fd.append("id_lavoratore", form.id_lavoratore);
            if (form.titolo)             fd.append("titolo", form.titolo);
            if (form.responsabile)       fd.append("responsabile", form.responsabile);
            if (form.esito)              fd.append("esito", form.esito);
            if (form.livello_rischio)    fd.append("livello_rischio", form.livello_rischio);
            if (form.numero_protocollo)   fd.append("numero_protocollo", form.numero_protocollo);
            if (form.data_valutazione)   fd.append("data_valutazione", form.data_valutazione);
            if (form.data_scadenza)      fd.append("data_scadenza", form.data_scadenza);
            if (form.periodicita_mesi)   fd.append("periodicita_mesi", form.periodicita_mesi);
            if (form.note)               fd.append("note", form.note);
            if (file)                    fd.append("file", file);
            await SafetyValutazioniService.create(fd);
            setMsg("Valutazione registrata.");
            setShow(false);
            setForm(EMPTY);
            setFile(null);
            await loadItems();
        } catch (err) {
            setError(err?.message || "Errore nel salvataggio.");
        } finally {
            setSaving(false);
        }
    };

    const onDownload = async (v) => {
        setError(null);
        try {
            const fn = `${(v.titolo || v.tipo_denominazione || "valutazione").toString().replace(/[^\w\-]+/g, "_")}.pdf`;
            await SafetyValutazioniService.download(v.id_valutazione, fn);
        } catch (err) {
            setError(err?.message || "Impossibile scaricare il file.");
        }
    };

    const onDelete = async (v) => {
        if (!v?.id_valutazione) return;
        if (!window.confirm("Eliminare questa valutazione?")) return;
        try {
            await SafetyValutazioniService.deleteValutazione(v.id_valutazione);
            await loadItems();
        } catch (err) {
            setError(err?.message || "Errore nell'eliminazione.");
        }
    };

    // Report output: apre una finestra stampabile (salvabile in PDF dal
    // dialogo di stampa del browser) con l'elenco completo delle valutazioni.
    const esc = (s) => String(s ?? "").replace(/[&<>"]/g, c => (
        { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]
    ));

    const printReport = () => {
        const oggi = new Date().toLocaleDateString("it-IT");
        const righe = items.map(v => `
            <tr>
                <td>${esc(v.titolo || v.tipo_denominazione || "—")}</td>
                <td>${esc(v.tipo_categoria || "")}</td>
                <td>${esc(v.responsabile || "")}</td>
                <td>${esc((ESITI.find(e => e.value === v.esito)?.label) || v.esito || "")}</td>
                <td>${esc(v.livello_rischio || "")}</td>
                <td>${esc(fmtDate(v.data_valutazione))}</td>
                <td>${esc(fmtDate(v.data_scadenza))}</td>
            </tr>`).join("");
        const html = `<!DOCTYPE html><html lang="it"><head><meta charset="utf-8">
            <title>Report valutazioni dei rischi</title>
            <style>
                body { font-family: Arial, Helvetica, sans-serif; color: #222; margin: 24px; }
                h1 { font-size: 18px; margin: 0 0 4px; }
                .meta { color: #666; font-size: 12px; margin-bottom: 16px; }
                table { width: 100%; border-collapse: collapse; font-size: 12px; }
                th, td { border: 1px solid #bbb; padding: 6px 8px; text-align: left; vertical-align: top; }
                th { background: #eef2f7; }
                @media print { .noprint { display: none; } }
            </style></head><body>
            <h1>Report valutazioni dei rischi</h1>
            <div class="meta">Documento generato il ${esc(oggi)} — ${items.length} valutazioni registrate</div>
            <table>
                <thead><tr>
                    <th>Valutazione</th><th>Categoria</th><th>Responsabile</th>
                    <th>Esito</th><th>Rischio residuo</th><th>Data</th><th>Revisione</th>
                </tr></thead>
                <tbody>${righe || '<tr><td colspan="7">Nessuna valutazione registrata.</td></tr>'}</tbody>
            </table>
            <p class="noprint" style="margin-top:16px;">
                <button onclick="window.print()">Stampa / Salva PDF</button>
            </p>
            <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 300); };<\/script>
            </body></html>`;
        const w = window.open("", "_blank");
        if (!w) {
            setError("Impossibile aprire la finestra del report: consenti i popup per questo sito.");
            return;
        }
        w.document.open();
        w.document.write(html);
        w.document.close();
    };

    const tipoSel = form.id_tipo_val ? tipi.find(t => Number(t.id_tipo_val) === Number(form.id_tipo_val)) : null;
    const tipoHelp = tipoSel ? VAL_HELP[tipoSel.codice] : null;
    const presentTipoIds = new Set(items.filter(i => i.id_tipo_val).map(i => Number(i.id_tipo_val)));
    const mancanti = tipi.filter(t => Number(t.obbligatorio) === 1 && !presentTipoIds.has(Number(t.id_tipo_val)));

    if (!idAzienda) {
        return (
            <BsAlert variant="info" className="py-2 mb-0">
                Salva prima i dati dell'azienda per gestire le valutazioni dei rischi.
            </BsAlert>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                    <i className="bi bi-clipboard-data me-2 text-info" />
                    Valutazioni dei rischi
                </h6>
                <div className="d-flex gap-2">
                    <Button size="sm" variant="outline-secondary" onClick={printReport} disabled={items.length === 0} title="Genera report stampabile / PDF">
                        <i className="bi bi-printer me-1" />
                        Report
                    </Button>
                    <Button size="sm" variant="primary" onClick={openForm} disabled={show}>
                        <i className="bi bi-plus-lg me-1" />
                        Aggiungi valutazione
                    </Button>
                </div>
            </div>

            <div className="text-muted small mb-2">
                Valutazioni <strong>specifiche</strong> integrative al DVR (non sostituiscono il documento nel tab Documenti):
                aggressione/rapina, lavoro notturno, stress L-C, VDT, lavoratore isolato, guida mezzi e altro.
                Il catalogo dipende dal comparto dell'azienda.
            </div>

            {mancanti.length > 0 && (
                <BsAlert variant="warning" className="py-2 small">
                    <strong>Valutazioni obbligatorie mancanti ({mancanti.length}):</strong>{" "}
                    {mancanti.map(m => m.denominazione).join(", ")}.
                </BsAlert>
            )}

            {error && <BsAlert variant="danger" className="py-2 small">{error}</BsAlert>}
            {msg && <BsAlert variant="success" className="py-2 small" onClose={() => setMsg(null)} dismissible>{msg}</BsAlert>}

            {show && (
                <div className="border rounded p-3 mb-3" style={{ backgroundColor: "#f8f9fa" }}>
                    <div className="fw-semibold mb-2">Nuova valutazione</div>
                    <Row className="g-2">
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Tipo valutazione</Form.Label>
                            <Form.Select size="sm" value={form.id_tipo_val} onChange={e => setForm(f => ({ ...f, id_tipo_val: e.target.value }))}>
                                <option value="">— Altro / personalizzato —</option>
                                {groupByCategoria(tipi).map(([cat, catItems]) => (
                                    <optgroup key={cat} label={cat}>
                                        {catItems.map(t => (
                                            <option key={t.id_tipo_val} value={t.id_tipo_val}>
                                                {t.denominazione}{Number(t.obbligatorio) === 1 ? " (obbligatoria)" : ""}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </Form.Select>
                            {tipoHelp && <Form.Text className="text-muted d-block">{tipoHelp}</Form.Text>}
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Titolo (se altro)</Form.Label>
                            <Form.Control size="sm" value={form.titolo} onChange={e => setForm(f => ({ ...f, titolo: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Responsabile (RSPP, consulente…)</Form.Label>
                            <Form.Control size="sm" value={form.responsabile} onChange={e => setForm(f => ({ ...f, responsabile: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Esito</Form.Label>
                            <Form.Select size="sm" value={form.esito} onChange={e => setForm(f => ({ ...f, esito: e.target.value }))}>
                                {ESITI.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Livello rischio residuo</Form.Label>
                            <Form.Select size="sm" value={form.livello_rischio} onChange={e => setForm(f => ({ ...f, livello_rischio: e.target.value }))}>
                                {LIVELLI.map(l => <option key={l.value || "x"} value={l.value}>{l.label}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Riferito a lavoratore (opz.)</Form.Label>
                            <LavoratoreSearchPicker
                                idAzienda={idAzienda}
                                value={form.id_lavoratore}
                                onChange={v => setForm(f => ({ ...f, id_lavoratore: v }))}
                                allowEmpty
                                emptyLabel="— Valutazione aziendale —"
                                placeholder="Cerca lavoratore (opzionale)…"
                            />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Data valutazione</Form.Label>
                            <Form.Control type="date" size="sm" value={form.data_valutazione} onChange={e => setForm(f => ({ ...f, data_valutazione: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Prossima revisione</Form.Label>
                            <Form.Control type="date" size="sm" value={form.data_scadenza} onChange={e => setForm(f => ({ ...f, data_scadenza: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Periodicità revisione (mesi)</Form.Label>
                            <Form.Control type="number" size="sm" min="0" value={form.periodicita_mesi} onChange={e => setForm(f => ({ ...f, periodicita_mesi: e.target.value }))} placeholder={tipoSel?.periodicita_mesi || ""} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Protocollo / riferimento</Form.Label>
                            <Form.Control size="sm" value={form.numero_protocollo} onChange={e => setForm(f => ({ ...f, numero_protocollo: e.target.value }))} />
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small mb-0">Note</Form.Label>
                            <Form.Control as="textarea" rows={2} size="sm" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Allegato (PDF)</Form.Label>
                            <Form.Control type="file" size="sm" accept=".pdf,.PDF" onChange={e => setFile(e.target.files?.[0] || null)} />
                        </Col>
                    </Row>
                    <div className="mt-2 d-flex gap-2">
                        <Button size="sm" variant="primary" onClick={submit} disabled={saving}>
                            {saving ? <Spinner size="sm" animation="border" /> : "Salva valutazione"}
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => setShow(false)} disabled={saving}>Annulla</Button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-muted small"><Spinner size="sm" animation="border" className="me-2" />Caricamento…</div>
            ) : items.length === 0 ? (
                <div className="text-muted small">
                    Nessuna valutazione registrata. Il <strong>DVR</strong> si archivia nel tab Documenti;
                    qui si gestiscono le valutazioni specifiche del settore.
                </div>
            ) : (
                <Table size="sm" bordered hover responsive className="mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Valutazione</th>
                            <th>Esito</th>
                            <th>Data</th>
                            <th>Revisione</th>
                            <th>Stato</th>
                            <th className="text-end">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(v => (
                            <tr key={v.id_valutazione}>
                                <td>
                                    <div>{v.titolo || v.tipo_denominazione || "—"}</div>
                                    <div className="text-muted small">
                                        {v.tipo_categoria || ""}
                                        {v.responsabile ? `${v.tipo_categoria ? " · " : ""}${v.responsabile}` : ""}
                                        {v.lav_cognome ? ` · ${v.lav_cognome} ${v.lav_nome || ""}` : ""}
                                        {v.livello_rischio ? ` · Rischio ${v.livello_rischio}` : ""}
                                    </div>
                                </td>
                                <td>{esitoBadge(v.esito)}</td>
                                <td>{fmtDate(v.data_valutazione)}</td>
                                <td>{fmtDate(v.data_scadenza)}</td>
                                <td>{statoBadge(v.stato, v.giorni_alla_scadenza)}</td>
                                <td className="text-end">
                                    {v.path_file && (
                                        <Button size="sm" variant="danger" className="me-1" onClick={() => onDownload(v)}>
                                            <i className="bi bi-file-earmark-pdf me-1" />PDF
                                        </Button>
                                    )}
                                    <Button size="sm" variant="outline-danger" onClick={() => onDelete(v)}>
                                        <i className="bi bi-trash" />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </div>
    );
}
